import { createContext, useContext, useEffect, useState } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

type Theme = 'light' | 'dark'

export type ColorKey =
  | 'sky'
  | 'indigo'
  | 'violet'
  | 'rose'
  | 'pink'
  | 'orange'
  | 'emerald'
  | 'teal'
  | 'neon'

export interface ColorTheme {
  name: string
  preview: string
  // 11 space-separated RGB channel strings: 50→950
  vars: [string, string, string, string, string, string, string, string, string, string, string]
}

// ─── Color Themes ─────────────────────────────────────────────────────────────

export const COLOR_THEMES: Record<ColorKey, ColorTheme> = {
  sky: {
    name: 'Sky Blue',
    preview: '#0ea5e9',
    vars: ['240 249 255','224 242 254','186 230 253','125 211 252','56 189 248','14 165 233','2 132 199','3 105 161','7 89 133','12 74 110','8 47 73'],
  },
  indigo: {
    name: 'Indigo',
    preview: '#6366f1',
    vars: ['238 242 255','224 231 255','199 210 254','165 180 252','129 140 248','99 102 241','79 70 229','67 56 202','55 48 163','49 46 129','30 27 75'],
  },
  violet: {
    name: 'Violet',
    preview: '#7c3aed',
    vars: ['245 243 255','237 233 254','221 214 254','196 181 253','167 139 250','139 92 246','124 58 237','109 40 217','91 33 182','76 29 149','46 16 101'],
  },
  rose: {
    name: 'Rose',
    preview: '#f43f5e',
    vars: ['255 241 242','255 228 230','254 205 211','253 164 175','251 113 133','244 63 94','225 29 72','190 18 60','159 18 57','136 19 55','76 5 25'],
  },
  pink: {
    name: 'Pink',
    preview: '#ec4899',
    vars: ['253 242 248','252 231 243','251 207 232','249 168 212','244 114 182','236 72 153','219 39 119','190 24 103','157 23 77','131 24 67','80 7 36'],
  },
  orange: {
    name: 'Orange',
    preview: '#f97316',
    vars: ['255 247 237','255 237 213','254 215 170','253 186 116','251 146 60','249 115 22','234 88 12','194 65 12','154 52 18','124 45 18','67 20 7'],
  },
  emerald: {
    name: 'Emerald',
    preview: '#10b981',
    vars: ['236 253 245','209 250 229','167 243 208','110 231 183','52 211 153','16 185 129','5 150 105','4 120 87','6 95 70','6 78 59','2 44 34'],
  },
  teal: {
    name: 'Teal',
    preview: '#14b8a6',
    vars: ['240 253 250','204 251 241','153 246 228','94 234 212','45 212 191','20 184 166','13 148 136','15 118 110','17 94 89','19 78 74','4 47 46'],
  },
  neon: {
    name: 'Neon',
    preview: '#06b6d4',
    vars: ['236 254 255','207 250 254','165 243 252','103 232 249','34 211 238','6 182 212','0 184 217','14 116 144','21 94 117','22 78 99','8 51 68'],
  },
}

const CSS_VAR_KEYS = [
  '--p-50','--p-100','--p-200','--p-300','--p-400',
  '--p-500','--p-600','--p-700','--p-800','--p-900','--p-950',
] as const

/** Apply the chosen color theme CSS variables to <html> immediately (synchronous). */
export function applyColorTheme(key: ColorKey) {
  const ct = COLOR_THEMES[key]
  const root = document.documentElement
  CSS_VAR_KEYS.forEach((cssVar, i) => {
    root.style.setProperty(cssVar, ct.vars[i])
  })
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  colorKey: ColorKey
  setColorTheme: (key: ColorKey) => void
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  colorKey: 'pink',
  setColorTheme: () => {},
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('celebrity-picks-theme') as Theme | null
  if (stored === 'light' || stored === 'dark') return stored
  return 'dark' // default to dark when the user hasn't chosen
}

function getInitialColor(): ColorKey {
  const stored = localStorage.getItem('celebrity-picks-color') as ColorKey | null
  return stored && COLOR_THEMES[stored] ? stored : 'pink'
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)
  const [colorKey, setColorKeyState] = useState<ColorKey>(() => {
    const initial = getInitialColor()
    // Apply immediately during initialization (before first render)
    applyColorTheme(initial)
    return initial
  })

  // Sync dark class to <html>
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    localStorage.setItem('celebrity-picks-theme', theme)
  }, [theme])

  // Persist color choice (CSS vars already applied synchronously in setColorTheme)
  useEffect(() => {
    localStorage.setItem('celebrity-picks-color', colorKey)
  }, [colorKey])

  // Listen for system preference changes when user hasn't manually overridden
  useEffect(() => {
    if (localStorage.getItem('celebrity-picks-theme')) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light')

  /** Applies CSS variables immediately (synchronous) then updates React state. */
  const setColorTheme = (key: ColorKey) => {
    applyColorTheme(key)   // ← synchronous, instant visual update
    setColorKeyState(key)  // ← React state for dropdown checkmark + persistence
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colorKey, setColorTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
